import React, { useState } from 'react'
import { Button, DatePicker, Select } from 'antd'
import dayjs from 'dayjs'
import { SlackOutlined } from '@ant-design/icons'
import {
  PaymentCollectionReportAPI,
  PaymentCollectionReportDateWiseAPI,
} from '../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import NotoFont from '../../Assets/Font/NotoSansDevanagari'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const PaymentCollectionReport = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))

  const [loading, setLoading] = useState(false)
  const [viewType, setViewType] = useState('excel')
  const [optionType, setOptionType] = useState('all')
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(dayjs())

  /* ================= FORMAT DATE ================= */

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return dayjs(dateString).format('DD-MM-YYYY')
  }

  /* ================= FETCH DATA ================= */

  const fetchOrders = async () => {
    if (!optionType || !selectedYear) return

    setLoading(true)

    try {
      let res

      if (optionType === 'all') {
        res = await axiosInstance.get(`${PaymentCollectionReportAPI}/${selectedYear}`)
      }

      if (optionType === 'date') {
        if (!fromDate || !toDate) return

        res = await axiosInstance.post(PaymentCollectionReportDateWiseAPI, {
          fromDate: fromDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD'),
        })
      }

      const result = Array.isArray(res?.data) ? res.data : []

      if (!result.length) {
        toast.info('No payment data found')
        return
      }

      if (viewType === 'excel') {
        generateExcel(result)
      } else {
        generatePDF(result)
      }
    } catch (err) {
      console.error(err)
      toast.error('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  /* ================= EXCEL ================= */

  // const generateExcel = (data) => {
  //   const rows = data.map((item) => ({
  //     'Receipt ID': item.receipt_id,
  //     'Receipt Date': formatDate(item.pay_receipt_date),
  //     'Customer Name': item.customer_name,
  //     Contact: item.customer_contact,
  //     'Bill Amount (Rs.)': Number(item.bill_amount || 0),
  //     'Received Amount (Rs.)': Number(item.received_amount || 0),
  //     'Payment Type': item.payment_type,
  //     'Payment Details': item.payment_details || '-',
  //   }))

  //   const totalReceivedAmount = rows.reduce(
  //     (sum, row) => sum + row['Received Amount (Rs.)'],
  //     0,
  //   )

  //   rows.push({
  //     'Receipt ID': '',
  //     'Receipt Date': '',
  //     'Customer Name': '',
  //     Contact: '',
  //     'Bill Amount (Rs.)': 'Grand Total',
  //     'Received Amount (Rs.)': totalReceivedAmount,
  //     'Payment Type': '',
  //     'Payment Details': '',
  //   })

  //   const worksheet = XLSX.utils.json_to_sheet(rows)
  //   const workbook = XLSX.utils.book_new()
  //   XLSX.utils.book_append_sheet(
  //     workbook,
  //     worksheet,
  //     'Payment Collection Report',
  //   )

  //   const excelBuffer = XLSX.write(workbook, {
  //     bookType: 'xlsx',
  //     type: 'array',
  //   })

  //   const blob = new Blob([excelBuffer], {
  //     type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  //   })

  //   const url = URL.createObjectURL(blob)
  //   window.open(url)
  // }

  const generateExcel = (data, fileName = 'Payment_Collection_Report') => {
    const rows = data.map((item) => ({
      'Receipt ID': item.receipt_id,
      'Receipt Date': formatDate(item.pay_receipt_date),
      'Customer Name': item.customer_name,
      Contact: item.customer_contact,
      'Bill Amount (Rs.)': Number(item.bill_amount || 0),
      'Received Amount (Rs.)': Number(item.received_amount || 0),
      'Payment Type': item.payment_type,
      'Payment Details': item.payment_details || '-',
    }))

    const totalReceivedAmount = rows.reduce(
      (sum, row) => sum + row['Received Amount (Rs.)'],
      0,
    )

    rows.push({
      'Receipt ID': '',
      'Receipt Date': '',
      'Customer Name': '',
      Contact: '',
      'Bill Amount (Rs.)': 'Grand Total',
      'Received Amount (Rs.)': totalReceivedAmount,
      'Payment Type': '',
      'Payment Details': '',
    })

    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Payment Collection Report',
    )

    //  Generate Current Date (DD-MM-YYYY)
    const today = new Date()
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(
      today.getMonth() + 1,
    ).padStart(2, '0')}-${today.getFullYear()}`

    const finalFileName = `${fileName} (${formattedDate}).xlsx`

    //  Direct Download
    XLSX.writeFile(workbook, finalFileName)
  }

  /* ================= PDF (AutoTable) ================= */
  const generatePDF = (data) => {
    //  Portrait A4
    const doc = new jsPDF('p', 'mm', 'a4')

    //  Marathi Font Support
    doc.addFileToVFS('NotoSansDevanagari.ttf', NotoFont)
    doc.addFont('NotoSansDevanagari.ttf', 'NotoSans', 'normal')
    doc.setFont('NotoSans')

    const totalReceived = data.reduce(
      (sum, item) => sum + Number(item.received_amount || 0),
      0,
    )

    const tableColumns = [
      'Receipt ID',
      'Receipt Date',
      'Customer Name',
      'Contact',
      'Bill Amount',
      'Received Amount',
      'Payment Type',
      'Payment Details',
    ]

    const tableRows = data.map((item) => [
      item.receipt_id,
      formatDate(item.pay_receipt_date),
      item.customer_name,
      item.customer_contact,
      item.bill_amount || 0,
      item.received_amount || 0,
      item.payment_type,
      item.payment_details || '-',
    ])

    //  Grand Total Row
    tableRows.push(['', '', '', '', 'Grand Total', totalReceived, '', ''])

    //  Title (same style as other reports)
    doc.setFontSize(14)
    doc.text('PAYMENT COLLECTION REPORT', 14, 15)

    const today = new Date().toLocaleDateString('en-GB')

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 22,
      theme: 'grid',
      tableWidth: 'auto',

      styles: {
        font: 'NotoSans',
        fontSize: 8,
        cellPadding: 2,
      },

      headStyles: {
        fillColor: [255, 102, 0],
      },

      margin: { left: 10, right: 10 },

      didDrawPage: function () {
        const pageCount = doc.internal.getNumberOfPages()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        doc.setFontSize(9)

        //  Page Number
        doc.text(
          `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          pageWidth - 45,
          pageHeight - 8,
        )

        //  Generated Date
        doc.text(`(${today})`, pageWidth - 10, pageHeight - 8, {
          align: 'right',
        })
      },
    })

    const blob = doc.output('blob')
    window.open(URL.createObjectURL(blob), '_blank')
  }

  /* ================= UI ================= */

  return (
    <div className="mainContainer">
      {loading && (
        <div className="fullscreen-loader">
          <div className="loader-box">
            <div className="spinner"></div>
            <p>Generating Report...</p>
          </div>
        </div>
      )}

      <div className="header-raw">
        <h3>
          <SlackOutlined style={{ color: 'orangered' }} /> Payment Collection
          Report
        </h3>
      </div>

      <div className="row p-2 m-0 mb-2 rounded border shadow">
        <div className="col-md-2 pt-2">
          <label>View Type</label>
          <Select
            style={{ width: '100%' }}
            value={viewType}
            onChange={(value) => {
              setViewType(value)
              setOptionType('all')
            }}
          >
            <Option value="excel">Excel</Option>
            <Option value="pdf">PDF</Option>
          </Select>
        </div>

        <div className="col-md-2 pt-2">
          <label>Options</label>
          <Select
            style={{ width: '100%' }}
            value={optionType}
            onChange={(value) => setOptionType(value)}
          >
            <Option value="all">All</Option>
            <Option value="date">Date Wise</Option>
          </Select>
        </div>

        {optionType === 'date' && (
          <>
            <div className="col-md-2 pt-2">
              <label>From Date</label>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                format="DD-MM-YYYY"
              />
            </div>

            <div className="col-md-2 pt-2">
              <label>To Date</label>
              <DatePicker
                value={toDate}
                onChange={setToDate}
                format="DD-MM-YYYY"
              />
            </div>
          </>
        )}

        <div className="col-md-2 pt-2 d-flex align-items-end">
          <Button
            type="primary"
            className="rounded"
            loading={loading}
            onClick={fetchOrders}
          >
            Show
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PaymentCollectionReport
